package twist.mena.sports.admin.components

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.files.File
import org.w3c.files.FileReader
import twist.mena.sports.admin.api.AdminLegacyCrudApi
import twist.mena.sports.admin.theme.*
import twist.mena.sports.shared.dto.admin.legacy.AdminLegacyEntityResponse
import twist.mena.sports.shared.dto.admin.legacy.CreateLegacyEntityRequest
import twist.mena.sports.shared.dto.admin.legacy.UpdateLegacyEntityRequest
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

@Composable
fun LegacyCrudEntityDialog(
    entity: AdminLegacyEntityResponse?,
    entityType: String,
    entityLabel: String,
    api: AdminLegacyCrudApi,
    onSaved: () -> Unit,
    onClose: () -> Unit
) {
    val colors = AppTheme.colors
    val isEditMode = entity != null
    var nameEn by remember(entity) { mutableStateOf(entity?.nameEn ?: "") }
    var nameAr by remember(entity) { mutableStateOf(entity?.nameAr ?: "") }
    var displayOrder by remember(entity) { mutableStateOf(entity?.displayOrder?.toString() ?: "") }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var currentImageUrl by remember(entity) { mutableStateOf(entity?.imageUrl) }
    val scope = rememberCoroutineScope()

    Modal(
        title = if (isEditMode) "Edit $entityLabel" else "Create $entityLabel",
        isOpen = true,
        onClose = onClose
    ) {
        FormField("English Name") {
            Input(InputType.Text) {
                value(nameEn)
                onInput { nameEn = it.value }
                style { formInputStyle(colors) }
            }
        }

        FormField("Arabic Name") {
            Input(InputType.Text) {
                value(nameAr)
                onInput { nameAr = it.value }
                style { formInputStyle(colors) }
            }
        }

        FormField("Display Order") {
            Input(InputType.Text) {
                value(displayOrder)
                onInput { displayOrder = it.value }
                style { formInputStyle(colors); width(120.px) }
            }
        }

        // Image section (edit mode only)
        if (isEditMode && entity != null) {
            ImageUpload(
                currentImageUrl = currentImageUrl,
                entityId = entity.id,
                onUpload = { file ->
                    val bytes = readFileAsBytes(file)
                    val contentType = file.asDynamic().type as? String ?: "image/jpeg"
                    api.uploadImage(entityType, entity.id, bytes, file.name, contentType)
                    onSaved()
                    // Reload entity to get new image URL — for now just mark as uploaded
                    currentImageUrl = "uploaded" // Will be refreshed when dialog reopens
                },
                onRemove = {
                    api.deleteImage(entityType, entity.id)
                    currentImageUrl = null
                    onSaved()
                }
            )
        } else if (!isEditMode) {
            Div({
                style {
                    padding(Spacing.md)
                    backgroundColor(Color(colors.surfaceHover))
                    borderRadius(BorderRadius.md)
                    marginBottom(Spacing.lg)
                    fontSize(Typography.sm)
                    color(Color(colors.textMuted))
                    themeTransition()
                }
            }) {
                Text("Image can be uploaded after creating the $entityLabel.")
            }
        }

        // Error
        error?.let { msg ->
            Div({
                style { errorMessageStyle(colors) }
            }) { Text(msg) }
        }

        // Action buttons
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.End)
                gap(Spacing.md)
                marginTop(Spacing.xl)
            }
        }) {
            Button({
                onClick { onClose() }
                style { secondaryButtonStyle(colors) }
            }) { Text("Cancel") }

            Button({
                if (saving) disabled()
                onClick {
                    scope.launch {
                        saving = true
                        error = null
                        try {
                            if (isEditMode && entity != null) {
                                api.updateEntity(
                                    entityType, entity.id,
                                    UpdateLegacyEntityRequest(
                                        nameEn = nameEn.ifBlank { null },
                                        nameAr = nameAr.ifBlank { null },
                                        displayOrder = displayOrder.toIntOrNull()
                                    )
                                )
                            } else {
                                api.createEntity(
                                    entityType,
                                    CreateLegacyEntityRequest(
                                        nameEn = nameEn.ifBlank { null },
                                        nameAr = nameAr.ifBlank { null },
                                        displayOrder = displayOrder.toIntOrNull()
                                    )
                                )
                            }
                            onSaved()
                            onClose()
                        } catch (e: Exception) {
                            error = e.message ?: "Operation failed"
                        } finally {
                            saving = false
                        }
                    }
                }
                style { primaryButtonStyle(colors, disabled = saving) }
            }) { Text(if (saving) "Saving..." else if (isEditMode) "Save Changes" else "Create") }
        }
    }
}

private suspend fun readFileAsBytes(file: File): ByteArray {
    return suspendCancellableCoroutine { cont ->
        val reader = FileReader()
        reader.onload = {
            val arrayBuffer = reader.asDynamic().result
            val uint8Array = js("new Uint8Array(arrayBuffer)")
            val length = uint8Array.length as Int
            val bytes = ByteArray(length) { i -> (uint8Array[i] as Number).toByte() }
            cont.resume(bytes)
        }
        reader.onerror = {
            cont.resumeWithException(Exception("Failed to read file"))
        }
        reader.asDynamic().readAsArrayBuffer(file)
    }
}
